/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Checkbox, ListSubheader } from '@mui/material';
import { useFormikContext } from 'formik';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import type { Props as TextFieldProps } from 'src/components/ui/TextField';
import TextField from 'src/components/ui/TextField';

import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import {
    usePropertyTypesItems,
    type Item,
    type PropertyTypeGroup,
    type FormValues,
} from '../hooks/usePropertyTypesItems';

type FormValuesWithType = FormValues & {
    type: 'rent' | 'sell';
};

const StyledListSubheader = styledComponent(ListSubheader)`
    padding: 0.5rem 1rem;
`;

const StyledMenuItem = styledComponent(MenuItem)<{
    $isSelected?: boolean;
}>`
    height: 2.5rem;
    ${({ $isSelected }) => $isSelected && `background-color: ${colours.grey.G100};`}
    span {
        padding-left: 0;
    }
`;

interface Props extends Omit<TextFieldProps, 'onChange'> {
    onChange?: (newValues: Partial<FormValues>) => void;
    groupTypesToDisplay?: PropertyTypeGroup[];
}
export const PropertyTypesSelect: React.FC<Props> = ({
    name,
    onChange,
    groupTypesToDisplay = ['furnished', 'advertiser', 'housing', 'oldOrNew'],
    ...rest
}) => {
    const { t } = useTranslation(['ad']);
    const { values, setFieldValue } = useFormikContext<FormValuesWithType>();

    const items = usePropertyTypesItems({
        formValues: values,
        setFieldValue,
        onItemValueChange: onChange,
        groupTypesToDisplay,
    });

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const changedValue = (event.target.value as any[]).pop() as Item['value'];
        const item = items.find((_item) => _item.type === 'Item' && _item.value === changedValue) as Item | undefined;
        item?.toggle();
    };

    const selectedItems = items.filter((item) => item.type === 'Item' && item.isChecked);
    const defaultValue = { label: 'propertyType' };

    return (
        <TextField
            id={name}
            name={name}
            SelectProps={{
                ...rest.SelectProps,
                multiple: true,
                value: selectedItems.length > 0 ? selectedItems : [defaultValue],
                renderValue(selected) {
                    const _selectedItems = selected as Item[] | null;
                    if (_selectedItems?.length === 1) {
                        return t(`ad:propertyTypes.${_selectedItems[0].label}`);
                    } else {
                        return `${t(`ad:propertyTypes.propertyType`)} (${_selectedItems?.length})`;
                    }
                },
                onChange: handleChange,
            }}
            select
        >
            {items.map((item, index) => {
                if (item.type === 'GroupItem') {
                    return (
                        <StyledListSubheader key={`PropertyTypeSelect-${index}`}>
                            <Typography variant="h5">{t(`ad:propertyTypes.${item.label}`)}</Typography>
                        </StyledListSubheader>
                    );
                } else {
                    return (
                        <StyledMenuItem
                            key={`PropertyTypeSelect-${index}`}
                            value={item.value}
                            $isSelected={item.isChecked}
                        >
                            <Checkbox checked={item.isChecked} />
                            <Typography variant="body1">{t(`ad:propertyTypes.${item.label}`)}</Typography>
                        </StyledMenuItem>
                    );
                }
            })}
        </TextField>
    );
};
